/**
 * Spec 015 — an idea's decision history and returning it to the discussion.
 *
 * A rejected idea goes back by its author or by the partner who rejected it; a
 * withdrawn one only by its author. The server checks the same rule; here it
 * only decides whether the button is offered. After a return the idea is `new`
 * again and the partner reviews it — the author still cannot approve it.
 */

import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { Ideas } from "@/api/resources";
import type { IdeaDecisionOut, IdeaOut } from "@/api/types";
import { Button } from "@/components/ui/Button";
import { TextArea } from "@/components/ui/Field";
import { useToast } from "@/components/ui/Toast";
import { formatDateTime, parseDate } from "@/lib/format";
import { Markdown } from "@/lib/markdown";

const WHAT: Record<IdeaDecisionOut["kind"], string> = {
  rejected: "Отклонена",
  withdrawn: "Отозвана автором",
  returned: "Возвращена в обсуждение",
  converted: "Принята",
};

function when(value: string): string {
  const at = parseDate(value);
  return at ? formatDateTime(at) : "";
}

export function decisionLine(d: IdeaDecisionOut): string {
  return `${WHAT[d.kind]} · ${d.actor_name ?? "участник"} · ${when(d.created_at)}`;
}

/** The last return, if the idea is open again after one. */
export function lastReturn(idea: IdeaOut): { returned: IdeaDecisionOut; before?: IdeaDecisionOut } | null {
  const decisions = idea.decisions ?? [];
  const last = decisions[decisions.length - 1];
  if (!last || last.kind !== "returned" || !["new", "reviewing"].includes(idea.status)) return null;
  return { returned: last, before: decisions[decisions.length - 2] };
}

/** «↩️ Возвращена · была отклонена 24.09 · Александр». */
export function ReturnedBadge({ idea }: { idea: IdeaOut }) {
  const info = lastReturn(idea);
  if (!info) return null;
  const before = info.before
    ? ` · была ${info.before.kind === "rejected" ? "отклонена" : "отозвана"} ${when(info.before.created_at)}` +
      (info.before.actor_name ? ` · ${info.before.actor_name}` : "")
    : "";
  return <p className="text-meta text-accent">↩️ Возвращена{before}</p>;
}

export function IdeaDecisions({ idea }: { idea: IdeaOut }) {
  const decisions = idea.decisions ?? [];
  if (!decisions.length) return null;
  return (
    <div className="space-y-2">
      <span className="text-meta text-hint">История решений</span>
      <ol className="space-y-2">
        {decisions.map((d) => (
          <li key={d.id} className="rounded-card bg-sbg p-2">
            <p className="text-meta text-hint">{decisionLine(d)}</p>
            {d.comment_md && <Markdown className="text-body" text={d.comment_md} />}
            {d.confirmation_md && <p className="text-meta text-hint">По просьбе: «{d.confirmation_md}»</p>}
          </li>
        ))}
      </ol>
    </div>
  );
}

export function mayReturn(idea: IdeaOut, myUserId?: string): boolean {
  if (!myUserId) return false;
  if (idea.status === "withdrawn") return idea.author_id === myUserId;
  if (idea.status === "rejected") return idea.author_id === myUserId || idea.reviewer_id === myUserId;
  return false;
}

export function ReopenIdea({ idea, myUserId, onDone }: { idea: IdeaOut; myUserId?: string; onDone?: () => void }) {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("");
  const toast = useToast();
  const qc = useQueryClient();
  const reopen = useMutation({
    mutationFn: () => Ideas.reopen(idea.id, reason.trim(), idea.version),
    onSuccess: () => {
      toast.success("Идея снова в обсуждении. Партнёр получит уведомление.");
      setOpen(false);
      setReason("");
      onDone?.();
    },
    onError: (error: Error) => toast.error(error.message),
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: ["ideas"] });
      void qc.invalidateQueries({ queryKey: ["knowledge-source"] });
      void qc.invalidateQueries({ queryKey: ["chat"] });
      void qc.invalidateQueries({ queryKey: ["memory-stats"] });
    },
  });
  if (!mayReturn(idea, myUserId)) return null;
  if (!open) {
    return (
      <Button variant="secondary" block onClick={() => setOpen(true)}>
        ↩️ Вернуть в обсуждение
      </Button>
    );
  }
  return (
    <div className="space-y-2">
      <TextArea label="Почему возвращаешь" value={reason} onChange={(e) => setReason(e.target.value)}
        placeholder="Что изменилось с прошлого решения?" rows={3} autoFocus disabled={reopen.isPending} />
      <p className="text-meta text-hint">
        Идея снова попадёт на ревью к партнёру. Прежнее решение и его причина останутся в истории.
      </p>
      {reopen.isError && <p role="alert" className="text-meta text-dstr">{reopen.error.message}</p>}
      <div className="flex gap-2">
        <Button loading={reopen.isPending} disabled={!reason.trim()} onClick={() => reopen.mutate()}>
          Вернуть
        </Button>
        <Button variant="secondary" disabled={reopen.isPending} onClick={() => setOpen(false)}>Отмена</Button>
      </div>
    </div>
  );
}
